import {
  ListingComparableSetFragment,
  ListingFragment,
  MlsListingFiltersInput,
  MlsListingFragment,
  MlsListingStatus,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import { bbox, buffer, distance, point } from '@turf/turf';
import dayjs from 'dayjs';
import { getListingPrice } from '~/apps/consumer/utils/listing.utils';
import { NormalizedPropertyDetails } from '../../Listings/ListingPage/utils';
import { PRICE_RANGE_OPTIONS } from '../../MapSearch/Filters/constants';
import { formatRangeCurrencyValue } from '../KeyTermsAndContingencies/common.utils';

export const getDateDiff = ({
  listingDate,
  minCompDate,
  unit = 'day',
}: {
  listingDate: dayjs.Dayjs;
  minCompDate: dayjs.Dayjs;
  unit?: 'day' | 'month';
}) => {
  const diff = listingDate.diff(minCompDate, unit);
  if (diff === 0 && unit === 'month') {
    return listingDate.diff(minCompDate, 'day');
  }

  return diff;
};

export const getDistanceInMiles = (
  firstMlsListing?: MlsListingFragment,
  secondMlsListing?: MlsListingFragment
) => {
  if (!firstMlsListing?.geo?.location || !secondMlsListing?.geo?.location) {
    return undefined;
  }

  return distance(
    point([
      firstMlsListing.geo?.location?.lon,
      firstMlsListing.geo?.location?.lat,
    ]),
    point([
      secondMlsListing.geo?.location?.lon,
      secondMlsListing.geo?.location?.lat,
    ]),
    { units: 'miles' }
  );
};

export const getMaxCompDistance = (
  comparableSet?: ListingComparableSetFragment,
  listing?: NormalizedPropertyDetails
) => {
  if (!listing?.location || !comparableSet) {
    return null;
  }

  return comparableSet?.listingComparables.reduce((max, comparable) => {
    const distanceInMiles =
      listing?.location && comparable.mlsListing?.geo?.location
        ? distance(
            point([listing?.location?.lon, listing?.location?.lat]),
            point([
              comparable.mlsListing?.geo?.location?.lon,
              comparable.mlsListing?.geo?.location?.lat,
            ]),
            { units: 'miles' }
          )
        : 0;

    return Math.max(max, distanceInMiles);
  }, 0);
};

export const getInitialCompsBBox = (
  comparableSet?: ListingComparableSetFragment,
  listing?: NormalizedPropertyDetails,
  radiusPadding = 1
) => {
  if (!listing?.location || !comparableSet) {
    return null;
  }

  const maxDistance = getMaxCompDistance(comparableSet, listing);

  const listingPoint = point([listing?.location?.lon, listing?.location?.lat]);

  const buffered = buffer(listingPoint, (maxDistance || 0) + radiusPadding, {
    units: 'miles',
  });

  return buffered ? bbox(buffered) : null;
};

export const getIntitialFilterValues = (
  listing?: ListingFragment | PublicListingFragment
): MlsListingFiltersInput => {
  if (!listing) {
    return {};
  }
  const listPrice = listing.mlsListing?.listPrice;

  const findClosestMinOption = (
    value: number | undefined,
    options: { label: string; value: number }[]
  ) => {
    return value !== undefined
      ? options.reduce((closest, option) => {
          if (option.value <= value) {
            return closest !== undefined
              ? Math.max(closest, option.value)
              : option.value;
          }

          return closest;
        }, undefined)
      : undefined;
  };

  const findClosestMaxOption = (
    value: number | undefined,
    options: { label: string; value: number }[]
  ) => {
    return value !== undefined
      ? options.reduce((closest, option) => {
          if (option.value >= value) {
            return closest !== undefined
              ? Math.min(closest, option.value)
              : option.value;
          }

          return closest;
        }, undefined)
      : undefined;
  };

  const priceRangeMin = findClosestMinOption(
    listPrice !== undefined ? listPrice * 0.8 : undefined,
    PRICE_RANGE_OPTIONS
  );
  const priceRangeMax = findClosestMaxOption(
    listPrice !== undefined ? listPrice * 1.2 : undefined,
    PRICE_RANGE_OPTIONS
  );

  const closedDateMin = dayjs().subtract(6, 'month').toDate();

  return {
    closedDateRange: {
      startDate: closedDateMin,
      endDate: undefined,
    },
    // TODO: add closed status to the filters conditionally
    statuses: [MlsListingStatus.Closed],
    ...(listPrice !== undefined
      ? {
          priceRange: {
            min: priceRangeMin,
            max: priceRangeMax,
          },
        }
      : {}),
  };
};

export const getCompsPriceRange = (
  comparableSet?: ListingComparableSetFragment
) => {
  const numberOfComps = comparableSet?.listingComparables.length || 0;

  if (numberOfComps < 1) {
    return undefined;
  }

  const minCompPrice = comparableSet?.listingComparables.reduce(
    (min, comparable) => {
      return Math.min(
        min,
        getListingPrice(comparable.mlsListing) +
          (comparable?.adjustments?.totalAdjustmentPrice || 0)
      );
    },
    Infinity
  );
  const maxCompPrice = comparableSet?.listingComparables.reduce(
    (max, comparable) => {
      return Math.max(
        max,
        getListingPrice(comparable.mlsListing) +
          (comparable?.adjustments?.totalAdjustmentPrice || 0)
      );
    },
    -Infinity
  );

  return numberOfComps > 1
    ? `${formatRangeCurrencyValue(minCompPrice)} - ${formatRangeCurrencyValue(
        maxCompPrice
      )}`
    : formatRangeCurrencyValue(minCompPrice);
};
